import React, { useEffect, useRef, useState } from 'react'
import ColorPicker from 'react-simple-colorpicker'
import './color.css'
import chroma from 'chroma-js'

// Lit une couleur enregistrée au format du champ (`rgb`, `rgba` ou `argb` : #AARRGGBB) ; null si absente ou illisible.
const parseColor = (value, format) => {
  if (!value) return null
  const hex = format === 'argb' && value.length === 9 ? '#' + value.substr(3, 6) + value.substr(1, 2) : value
  try {
    return chroma(hex)
  } catch (e) {
    return null
  }
}

// Hexa saisi (avec ou sans #, 3 ou 6 chiffres) ; null s'il n'est pas valide.
const parseHex = text => (/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(text.trim()) ? chroma(text.trim()) : null)

const toHexText = color =>
  color
    ? color
        .hex('rgb')
        .substr(1)
        .toUpperCase()
    : ''
const toOpacityText = color => (color ? String(Math.round(color.alpha() * 100)) : '')

/**
 * Sélecteur de couleur façon outil graphique : une pastille (sur damier, pour lire la transparence) qui ouvre
 * le nuancier en surimpression, la valeur hexa éditable et, pour les formats `rgba`/`argb`, l'opacité en pourcentage.
 * Les saisies texte ne s'appliquent qu'à la validation (Entrée ou sortie du champ) et reviennent à la valeur
 * courante si elles sont invalides. La valeur est enregistrée au format de `ui:options.format` (`rgb` par défaut).
 */
export default function Color({ schema = {}, uiSchema = {}, formData, onChange, disabled }) {
  const format = uiSchema['ui:options']?.format || 'rgb'
  const withAlpha = format === 'rgba' || format === 'argb'
  const color = parseColor(formData || schema.default, format)

  const [open, setOpen] = useState(false)
  const [hexText, setHexText] = useState(toHexText(color))
  const [opacityText, setOpacityText] = useState(toOpacityText(color))
  const containerRef = useRef()

  const colorKey = color ? color.hex('rgba') : ''
  useEffect(() => {
    setHexText(toHexText(color))
    setOpacityText(toOpacityText(color))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorKey])

  useEffect(() => {
    if (!open) return
    const closeOnOutside = event => !containerRef.current?.contains(event.target) && setOpen(false)
    const closeOnEscape = event => event.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', closeOnOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const save = next => onChange(next ? next.hex(format) : undefined)

  const commitHex = () => {
    const parsed = parseHex(hexText)
    if (parsed) save(parsed.alpha(color ? color.alpha() : 1))
    else setHexText(toHexText(color))
  }

  const commitOpacity = () => {
    const opacity = Number(opacityText)
    if (color && opacityText !== '' && opacity >= 0 && opacity <= 100) save(color.alpha(opacity / 100))
    else setOpacityText(toOpacityText(color))
  }

  const commitOnEnter = commit => event => {
    if (event.key !== 'Enter') return
    event.preventDefault() // Entrée validerait sinon tout le formulaire.
    commit()
  }

  return (
    <div className="form-group field color-field" ref={containerRef}>
      <label className="control-label">{schema.title}</label>
      <div className="color-field-row">
        <button
          type="button"
          className={'color-field-swatch' + (color ? '' : ' empty')}
          title={color ? 'Modifier la couleur' : 'Choisir une couleur'}
          disabled={disabled}
          onClick={() => setOpen(!open)}
        >
          <span>
            <span style={{ backgroundColor: color ? color.css() : undefined }} />
          </span>
        </button>
        <input
          className="form-control color-field-hex"
          value={hexText}
          placeholder="Aucune"
          maxLength={7}
          disabled={disabled}
          onChange={event => setHexText(event.target.value)}
          onBlur={commitHex}
          onKeyDown={commitOnEnter(commitHex)}
        />
        {withAlpha && color && (
          <div className="color-field-opacity">
            <input
              className="form-control"
              type="number"
              min={0}
              max={100}
              value={opacityText}
              disabled={disabled}
              onChange={event => setOpacityText(event.target.value)}
              onBlur={commitOpacity}
              onKeyDown={commitOnEnter(commitOpacity)}
            />
            <span>%</span>
          </div>
        )}
        {color && !disabled && (
          <button type="button" className="btn btn-link color-field-clear" title="Retirer la couleur" onClick={() => save(null)}>
            <i className="fa fa-times" />
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="color-field-popover">
          <ColorPicker
            color={(color || chroma('#ffffff')).css()}
            onChange={value => save(chroma(value))}
            opacitySlider={withAlpha}
          />
        </div>
      )}
    </div>
  )
}
